import { Text } from '../../../atoms/Text/Text';
import { Card } from '../../../molecules/Card/Card';
import type { KPIMetric } from '../../../molecules/KPI/KPI';
import { KPIText } from '../../../molecules/KPIText/KPIText';
import { cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';
import styles from './MetricSummaryCard.module.css';

/**
 * The four `Size=sm` figures across the top of Overview (972:20768–972:20771),
 * and the first four widgets of a custom report (994:5153).
 *
 * Three of the four are ordinary metrics over one row set, so they are one
 * component parameterised three ways (`TotalMentionsCard`,
 * `TotalUniqueAudienceCard`, `NegativeConversationCard`). The fourth is not:
 * share of voice needs two row sets, ours and the category's — see
 * `ShareOfVoiceKpiCard`.
 */

export type SummaryCardCopy = {
  title: string;
  helpText: string;
  helpSupportingText?: string;
};

export type MetricSummaryView = {
  /** The figure, with its delta badge when comparison is on. Its label is unused (the header is the label). */
  kpi: KPIMetric;
  /** Whether any mention is in scope. */
  hasRows: boolean;
  /**
   * The figures the one above is made of, on a line beneath it — the executive
   * `Overall sentiment` tile's positive and negative shares under net sentiment
   * (GP, 2026-11-03). Not in Figma. Bare figures: the badge is the headline's.
   */
  details?: { label: string; value: string }[];
};

export type MetricSummaryCardProps = WidgetBlockProps & {
  view?: MetricSummaryView;
};

/**
 * One metric, one figure, one delta badge — the card's own header is the label,
 * which is why the body is a bare `KPIText` rather than a `KPI` row.
 */
export function MetricSummaryCard({
  copy,
  status,
  view,
  span = 3,
  chrome,
  onRetry,
}: MetricSummaryCardProps & { copy: SummaryCardCopy }) {
  const hasData = view !== undefined && view.hasRows;
  return (
    <Card
      /* Set only when this card is a widget on a custom report — see `CardChrome`. */
      {...chrome}
      className={spanClass(span)}
      size="sm"
      {...cardState(status, hasData, onRetry)}
      title={copy.title}
      helpText={copy.helpText}
      helpSupportingText={copy.helpSupportingText}
      kpi={
        view &&
        (view.details?.length ? (
          <div className={styles.figures}>
            <KPIText tone={view.kpi.tone} badge={view.kpi.badge}>
              {view.kpi.value}
            </KPIText>
            <dl className={styles.details}>
              {view.details.map((d) => (
                <div key={d.label} className={styles.detail}>
                  <Text as="dt" size="sm" leading="none" color="secondary">
                    {d.label}
                  </Text>
                  <Text as="dd" size="sm" leading="none" weight="medium" color="primary">
                    {d.value}
                  </Text>
                </div>
              ))}
            </dl>
          </div>
        ) : (
          <KPIText tone={view.kpi.tone} badge={view.kpi.badge}>
            {view.kpi.value}
          </KPIText>
        ))
      }
    />
  );
}
